import React, { ReactNode } from 'react';
import * as SubframeCore from '@subframe/core';
import { Badge, Button, DropdownMenu } from 'subframe/index';
import { Icon, IconName } from '@subframe/core';
import { RouterLink } from 'components/RouterLink';
import { AtomicTooltip } from './AtomicTooltip';
import { isExternalLink } from 'components/nav-section';

interface ActionSection {
  title?: string;
  hidden?: boolean;
  items: ReactNode[];
}

interface ActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  sections: ActionSection[];
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

interface ActionItemProps {
  key: string;
  title: string;
  icon?: IconName;
  tooltip?: string;
  disabled?: boolean;
  link?: string;
  onClick?: (event: React.MouseEvent) => void;
  preview?: boolean;
  subscriptionLock?: boolean;
  subscriptionLockIcon?: boolean;
  className?: string;
}

const ActionItem = React.forwardRef<HTMLDivElement, ActionItemProps>(
  function ActionItem(
    {
      key,
      title,
      icon,
      tooltip,
      disabled,
      link,
      onClick,
      className,
      preview,
      subscriptionLock,
      subscriptionLockIcon,
      ...props
    },
    ref,
  ) {
    const Item = (
      <AtomicTooltip tooltipContent={tooltip}>
        <DropdownMenu.DropdownItem
          ref={ref}
          key={key}
          className={
            className +
            (disabled || preview || subscriptionLock
              ? ' opacity-50 cursor-not-allowed'
              : '')
          }
          icon={icon}
          onClick={(event) => {
            if (!disabled && !subscriptionLock) {
              if (link && isExternalLink(link)) {
                event.preventDefault();
                event.stopPropagation();
                window.open(link, '_blank');
                return;
              }

              onClick?.(event);
            }
          }}
          {...props}
        >
          <div className="flex items-center gap-2">
            {title}
            {preview && (
              <Badge variant="neutral" className="w-auto">
                Preview
              </Badge>
            )}
            {(subscriptionLock || subscriptionLockIcon) && (
              <Icon name="FeatherLock" className="text-subtext-color" />
            )}
          </div>
        </DropdownMenu.DropdownItem>
      </AtomicTooltip>
    );

    return link && !isExternalLink(link) ? (
      <RouterLink
        style={{ display: 'contents' }}
        to={!disabled && !subscriptionLock ? link : ''}
      >
        {Item}
      </RouterLink>
    ) : (
      Item
    );
  },
);

const ActionsRoot = React.forwardRef<HTMLElement, ActionsProps>(
  function ActionsRoot(
    { sections, size, className, ...otherProps }: ActionsProps,
    ref,
  ) {
    const handleContextMenu = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
    };

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
    };

    return (
      <SubframeCore.DropdownMenu.Root>
        <SubframeCore.DropdownMenu.Trigger asChild={true}>
          <Button
            className="w-28 flex-none"
            variant="neutral-secondary"
            size={size || 'medium'}
            iconRight="FeatherChevronDown"
            onClick={handleClick}
            onContextMenu={handleContextMenu}
            data-cy="actions-button"
          >
            Actions
          </Button>
        </SubframeCore.DropdownMenu.Trigger>
        <SubframeCore.DropdownMenu.Portal>
          <SubframeCore.DropdownMenu.Content
            side="bottom"
            align="end"
            sideOffset={4}
            asChild={true}
            onContextMenu={handleContextMenu}
            onClick={handleClick}
          >
            <DropdownMenu>
              <div className="flex w-full flex-col items-start gap-2">
                {sections
                  .filter((s) => !s.hidden)
                  .map((section, index) => (
                    <div
                      key={index}
                      className="flex w-full flex-col items-start justify-between"
                    >
                      {section.title && (
                        <div className="flex w-full flex-col items-start gap-1 px-3 py-2">
                          <span className="text-label-bold font-label-bold text-subtext-color">
                            {section.title}
                          </span>
                        </div>
                      )}
                      <div
                        className="flex w-full flex-col items-start"
                        onClick={handleClick}
                      >
                        {section.items}
                      </div>
                    </div>
                  ))}
              </div>
            </DropdownMenu>
          </SubframeCore.DropdownMenu.Content>
        </SubframeCore.DropdownMenu.Portal>
      </SubframeCore.DropdownMenu.Root>
    );
  },
);

export const Actions = Object.assign(ActionsRoot, {
  Item: ActionItem,
});
